import React, { useMemo, useContext, useState, useEffect } from "react";
import { Container, Row, Col, Button, Spinner, Alert } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import "./listing-calendar-main.scss";
import { v4 as uuidv4 } from 'uuid';
import { IdentityContext } from "../../../../context/identity-context";

import ListingCalendarService from "../../../../services/listing-calendar-service";
import ReservationRequestModal from "./components/modals/reservationRequest";
import BlockDateModal from "./components/modals/block-date-modal";
import useReservations from "../../../../hooks/use-reservations";
import { buildMonthGrid, formatYearMonth, parseYearMonthToken, formatDateKey } from "../../../../helpr/date-utils";
import CalendarGrid from "./components/listing-calendar-parts/calendar-grid";
import CalendarLegend from "../../../../components/listing-calendar/calendar-legend/calendar-legend";

const URL_PARAM = "month";

const IssuerListingCalendarMain = () => {
  const { user } = useContext(IdentityContext);
  const institutionCode = user.attributes['custom:entity_id']

  // helper to add months to a year/month pair
  const addMonths = (year, monthIndex, offset) => {
    const dt = new Date(year, monthIndex, 1);
    dt.setMonth(dt.getMonth() + offset);
    return { year: dt.getFullYear(), monthIndex: dt.getMonth() };
  };

  // clamp compare helpers
  const isBefore = (aYear, aMonth, bYear, bMonth) => {
    if (aYear < bYear) return true;
    if (aYear > bYear) return false;
    return aMonth < bMonth;
  };
  const isAfter = (aYear, aMonth, bYear, bMonth) => {
    if (aYear > bYear) return true;
    if (aYear < bYear) return false;
    return aMonth > bMonth;
  };
  const isEqual = (aYear, aMonth, bYear, bMonth) => aYear === bYear && aMonth === bMonth;

  // determine today's month as the minimum allowed month
  const today = new Date();
  const minMonth = { year: today.getFullYear(), monthIndex: today.getMonth() };
  const maxMonth = addMonths(minMonth.year, minMonth.monthIndex, 5); // inclusive: this month + 5 -> 6 months total

  const initMonth = () => {
    // try URL param but clamp to minMonth..maxMonth
    if (typeof window !== "undefined") {
      try {
        const params = new URLSearchParams(window.location.search);
        const urlToken = params.get(URL_PARAM);
        const parsedFromUrl = parseYearMonthToken(urlToken);
        if (parsedFromUrl) {
          // clamp parsedFromUrl
          if (isBefore(parsedFromUrl.year, parsedFromUrl.monthIndex, minMonth.year, minMonth.monthIndex)) {
            return minMonth;
          }
          if (isAfter(parsedFromUrl.year, parsedFromUrl.monthIndex, maxMonth.year, maxMonth.monthIndex)) {
            return maxMonth;
          }
          return parsedFromUrl;
        }
      } catch {}
    }
    // default to today's month
    return { year: today.getFullYear(), monthIndex: today.getMonth() };
  };

  const initialMonth = initMonth();
  const [currentYear, setCurrentYear] = useState(initialMonth.year);
  const [currentMonthIndex, setCurrentMonthIndex] = useState(initialMonth.monthIndex);

  const [selectedDateKey, setSelectedDateKey] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalMode, setModalMode] = useState("view");
  const [selectedBlockedItem, setSelectedBlockedItem] = useState(null);

  const listingCalendarService = useMemo(() => new ListingCalendarService(), []);

  const { reservations, setReservations, loading, error } = useReservations(listingCalendarService);

  // local state for blocked-dates fetch (server-provided blocked/holiday list)
  const [blockedItems, setBlockedItems] = useState([]);
  const [blockedLoading, setBlockedLoading] = useState(false);
  const [blockedError, setBlockedError] = useState(null);

  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      url.searchParams.set(URL_PARAM, formatYearMonth(currentYear, currentMonthIndex));
      window.history.replaceState({}, "", url.toString());
    } catch {}
  }, [currentYear, currentMonthIndex]);

  const weeks = useMemo(
    () => buildMonthGrid(currentYear, currentMonthIndex),
    [currentYear, currentMonthIndex]
  );

  function normalizeApiBlockedItem(apiItem, targetYears = [currentYear - 1, currentYear, currentYear + 1]) {
    const normalizedOutput = [];
    const rawKey = apiItem.listingDateKey || apiItem.listingDate || apiItem.date || null;

    const makeEntry = (dateKey, yearSuffixIfNeeded, markRegular = false) => {
      const idBase = apiItem.id || `BLOCKDATE#${uuidv4().replace(/-/g,'')}`;
      const id = String(idBase) + (yearSuffixIfNeeded ? `#${yearSuffixIfNeeded}` : "");
      return {
        id,
        sourceId: apiItem.id || null,
        listingDateKey: dateKey,
        isHoliday: !!apiItem.isHoliday,
        isRegular: !!apiItem.isRegular,
        notes: apiItem.reason || apiItem.notes || "",
        admin: apiItem.admin,
        status: "blocked",
        raw: apiItem,
      };
    };

    if (rawKey && /^\d{4}-\d{2}-\d{2}$/.test(rawKey)) {
      normalizedOutput.push(makeEntry(rawKey, null, false));
      return normalizedOutput;
    }

    if (rawKey) {
      try {
        const parsedDate = new Date(rawKey);
        if (!isNaN(parsedDate.getTime())) {
          normalizedOutput.push(makeEntry(formatDateKey(parsedDate), null, !!apiItem.isRegular));
          return normalizedOutput;
        }
      } catch {}
    }

    return normalizedOutput;
  }

  // load blocked items
  useEffect(() => {
    let mounted = true;
    async function loadBlocked() {
      setBlockedLoading(true);
      setBlockedError(null);
      try {
        const role = "issuer";
        const res = await listingCalendarService.getBlockedDates(role, institutionCode);
        const listFromApi = (res && res.blockDates) ? res.blockDates : (res && res.blockedDates) ? res.blockedDates : [];
        const targetYears = [currentYear - 1, currentYear, currentYear + 1];

        const normalized = [];
        listFromApi.forEach((apiBlocked) => {
          try {
            const entries = normalizeApiBlockedItem(apiBlocked, targetYears);
            entries.forEach((entry) => normalized.push(entry));
          } catch (normalizeError) {
            console.warn("Failed to normalize blocked item", apiBlocked, normalizeError);
          }
        });

        if (!mounted) return;

        const seen = new Set();
        const deduplicated = [];
        normalized.forEach((normalizedItem) => {
          const dedupeKey = `${normalizedItem.listingDateKey}::${normalizedItem.sourceId || normalizedItem.notes || normalizedItem.id}`;
          if (!seen.has(dedupeKey)) {
            seen.add(dedupeKey);
            deduplicated.push(normalizedItem);
          }
        });

        if (!mounted) return;
        setBlockedItems(deduplicated);
      } catch (err) {
        if (!mounted) return;
        console.error("Failed to load blocked dates", err);
        setBlockedError(err);
      } finally {
        if (!mounted) return;
        setBlockedLoading(false);
      }
    }
    loadBlocked();
    return () => { mounted = false; };
  }, [listingCalendarService, currentYear, currentMonthIndex]);

  const reservationsMap = useMemo(() => {
    const mapByDate = {};

    function ensureDateKeyExists(dateKey) {
      if (!mapByDate[dateKey]) mapByDate[dateKey] = [];
    }

    function pushOccurrence(dateKey, occurrence) {
      ensureDateKeyExists(dateKey);
      mapByDate[dateKey].push(occurrence);
    }

    reservations.forEach((reservation) => {
      try {
        if (reservation && reservation.listingDates) {
          if (Array.isArray(reservation.listingDates)) {
            reservation.listingDates.forEach((listingDateItem) => {
              const listingToken = listingDateItem?.date ?? listingDateItem;
              if (!listingToken) return;
              try {
                const dateObject = new Date(listingToken);
                if (isNaN(dateObject.getTime())) return;
                const occurrenceDateKey = formatDateKey(dateObject);
                pushOccurrence(occurrenceDateKey, {
                  id: reservation.id,
                  institution: reservation.institution,
                  email: reservation.email,
                  notes: reservation.notes,
                  status: listingDateItem?.status || reservation.status || "pending",
                  listingDateOriginal: listingDateItem?.date || String(listingDateItem),
                  listingDateKey: occurrenceDateKey,
                  listingDateStatus: listingDateItem?.status || reservation.status || "pending",
                  raw: reservation,
                });
              } catch {}
            });
          } else if (typeof reservation.listingDates === "string") {
            const tokens = reservation.listingDates.split(/[,;]+/).map((t) => t.trim()).filter(Boolean);
            tokens.forEach((tokenStr) => {
              try {
                const parsedDate = new Date(tokenStr);
                if (isNaN(parsedDate.getTime())) return;
                const occurrenceDateKey = formatDateKey(parsedDate);
                pushOccurrence(occurrenceDateKey, {
                  id: reservation.id,
                  institution: reservation.institution,
                  email: reservation.email,
                  notes: reservation.notes,
                  status: reservation.status,
                  listingDateOriginal: tokenStr,
                  listingDateKey: occurrenceDateKey,
                  listingDateStatus: reservation.status || "pending",
                  raw: reservation,
                });
              } catch {}
            });
          }
        }

        if (reservation && reservation.status === "blocked") {
          let blockedKey = reservation.listingDateKey || null;

          if (!blockedKey && typeof reservation.id === "string") {
            const match = reservation.id.match(/([0-9]{4}-[0-9]{2}-[0-9]{2})/);
            if (match) blockedKey = match[1];
          }

          if (!blockedKey && reservation.date) {
            try {
              const parsedDate = new Date(reservation.date);
              if (!isNaN(parsedDate.getTime())) blockedKey = formatDateKey(parsedDate);
            } catch {}
          }

          if (blockedKey) {
            pushOccurrence(blockedKey, {
              id: reservation.id,
              notes: reservation.notes || "",
              status: "blocked",
              isHoliday: typeof reservation.isHoliday === "boolean" ? reservation.isHoliday : !!reservation.holiday,
              listingDateKey: blockedKey,
              raw: reservation,
            });
          }
        }
      } catch (processingError) {
        console.warn("Error processing reservation entry for calendar map", processingError, reservation);
      }
    });

    blockedItems.forEach((blockedItem) => {
      if (!blockedItem || !blockedItem.listingDateKey) return;
      try {
        let dateKey = blockedItem.listingDateKey;
        if (!/^\d{4}-\d{2}-\d{2}$/.test(dateKey)) {
          try {
            const parsedDate = new Date(dateKey);
            if (!isNaN(parsedDate.getTime())) dateKey = formatDateKey(parsedDate);
            else return;
          } catch { return; }
        }

        pushOccurrence(dateKey, {
          id: blockedItem.id,
          notes: blockedItem.notes || "",
          status: "blocked",
          isHoliday: !!blockedItem.isHoliday,
          isRegular: !!blockedItem.isRegular,
          listingDateKey: dateKey,
          admin: blockedItem.admin,
          raw: blockedItem.raw || blockedItem,
        });
      } catch (e) {
        console.warn("Error adding blocked item to map", blockedItem, e);
      }
    });

    return mapByDate;
  }, [reservations, blockedItems]);

  const monthLabel = new Date(currentYear, currentMonthIndex, 1).toLocaleString(undefined, {
    month: "long",
    year: "numeric",
  });

  // navigation guards
  const canGoPrevious = !isBefore(currentYear, currentMonthIndex, minMonth.year, minMonth.monthIndex);
  // specifically disallow going to months before minMonth, and disable when equal to minMonth
  const atMinMonth = isEqual(currentYear, currentMonthIndex, minMonth.year, minMonth.monthIndex);
  const atMaxMonth = isEqual(currentYear, currentMonthIndex, maxMonth.year, maxMonth.monthIndex);

  function goToPreviousMonth() {
    if (atMinMonth) return;
    const dt = new Date(currentYear, currentMonthIndex - 1, 1);
    // if computed previous goes before minMonth, clamp to minMonth
    if (isBefore(dt.getFullYear(), dt.getMonth(), minMonth.year, minMonth.monthIndex)) {
      setCurrentYear(minMonth.year);
      setCurrentMonthIndex(minMonth.monthIndex);
      return;
    }
    setCurrentYear(dt.getFullYear());
    setCurrentMonthIndex(dt.getMonth());
  }
  function goToNextMonth() {
    // don't allow navigation beyond maxMonth
    const next = new Date(currentYear, currentMonthIndex + 1, 1);
    if (isAfter(next.getFullYear(), next.getMonth(), maxMonth.year, maxMonth.monthIndex)) {
      // clamp to max
      setCurrentYear(maxMonth.year);
      setCurrentMonthIndex(maxMonth.monthIndex);
      return;
    }
    setCurrentYear(next.getFullYear());
    setCurrentMonthIndex(next.getMonth());
  }

  function openModalForDate(date) {
    const dateKey = formatDateKey(date);
    const occurrences = reservationsMap[dateKey] || [];

    const blockedOccurrence = occurrences.find((occ) => occ && occ.status === "blocked");
    if (blockedOccurrence) {
      setSelectedDateKey(dateKey);
      setSelectedBlockedItem(blockedOccurrence);
      setModalMode("blocked");
      setIsModalVisible(true);
      return;
    }

    if (occurrences.length > 0) {
      setSelectedDateKey(dateKey);
      setSelectedBlockedItem(null);
      setModalMode("view");
      setIsModalVisible(true);
    } else {
      setSelectedDateKey(dateKey);
      setSelectedBlockedItem(null);
      setModalMode("block");
      setIsModalVisible(true);
    }
  }
  
  const reservationsForSelectedDate = selectedDateKey ? reservationsMap[selectedDateKey] || [] : [];

  // legend items (same structure as other calendar)
  const legendItems = [
    { key: 'reservation', label: 'Reservation' },
    // { key: 'blocked', label: 'Blocked (regular)' },
    { key: 'holiday', label: 'Holiday / Blocked (holiday)' }
  ];

  return (
    <Container className="issuer-listing-calendar-container p-3">
      <div className="calendar-card">
        <Row className="align-items-center calendar-header mb-2">
          <Col xs="auto">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={goToPreviousMonth}
              disabled={atMinMonth}
              aria-disabled={atMinMonth}
              title={atMinMonth ? "Cannot go earlier than this month" : "Previous month"}
            >
              {"<"}
            </Button>
          </Col>
          <Col className="text-center">
            <strong>{monthLabel}</strong>
          </Col>
          <Col xs="auto">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={goToNextMonth}
              disabled={atMaxMonth}
              aria-disabled={atMaxMonth}
              title={atMaxMonth ? "Reached max allowed month" : "Next month"}
            >
              {">"}
            </Button>
          </Col>
        </Row>

        {/* Legends (implemented via CalendarLegend component) */}
        <Row className="mb-2">
          <Col>
            <CalendarLegend items={legendItems} />
          </Col>
        </Row>

        {(loading || blockedLoading) && (
          <div className="text-center my-2">
            <Spinner animation="border" size="sm" /> Loading reservations...
          </div>
        )}
        {error && (
          <div className="mb-2">
            <Alert variant="danger" className="py-1">
              {String(error)}
            </Alert>
          </div>
        )}
        {blockedError && (
          <div className="mb-2">
            <Alert variant="warning" className="py-1">
              Failed to load blocked dates.
            </Alert>
          </div>
        )}

        <CalendarGrid
          weeks={weeks}
          currentMonthIndex={currentMonthIndex}
          reservationsMap={reservationsMap}
          onCellClick={openModalForDate}
        />
      </div>

      {modalMode === "view" && (
        <ReservationRequestModal
          show={isModalVisible}
          onHide={() => setIsModalVisible(false)}
          reservationsList={reservationsForSelectedDate}
          onAccept={(id, key) => {/* unchanged */}}
          onReject={(id, key) => {/* unchanged */}}
          selectedDateKey={selectedDateKey}
        />
      )}

      {modalMode === "block" && (
        <BlockDateModal
          show={isModalVisible}
          onHide={() => setIsModalVisible(false)}
          selectedDateKey={selectedDateKey}
        />
      )}

      {modalMode === "blocked" && (
        <BlockDateModal
          show={isModalVisible}
          onHide={() => setIsModalVisible(false)}
          selectedDateKey={selectedDateKey}
          blockedItem={selectedBlockedItem}
          readOnly={true}
        />
      )}
    </Container>
  );
};

export default IssuerListingCalendarMain;
